import React from "react";
import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";

import { Table, PageHeader, Card, Button, showSnackbar } from "@cofano/cck";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { TableId, useFrontendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";

import FleetShipListPopover from "./components/popover/FleetShipListPopover";

import { Language, usePermission } from "imports/Utils";

import { GetFleetsDocument, GetFleetsQuery } from "__generated__/view/fleets/graphql/fleet.queries.generated";
import { DeleteFleetDocument, DeleteFleetMutation } from "__generated__/view/fleets/graphql/fleet.mutations.generated";
import { ChildPropsFromPagination } from "types/commons";

type FleetFormQuery = ChildPropsFromPagination<GetFleetsQuery, "fleets">

const Fleets = () => {

	const navigate = useNavigate();
	const confirm = useConfirm();
	const { isReadOnly } = usePermission();

	const  { data, refetch, loading }  = useQuery<GetFleetsQuery>(GetFleetsDocument);

	const fleets = data?.fleets?.content ?? [];

	const { tableProps, refetchAfterDelete } = useFrontendTable({
		id: TableId.Fleets,
		content: fleets,
	});

	const [ removeFleet ] = useMutation<DeleteFleetMutation>(DeleteFleetDocument);

	const deleteFleet = (id: number) => {
		removeFleet({
			variables: {
				fleetId: id,
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_DELETED" }),  { variant: "success" });
			refetchAfterDelete();
			refetch();
		});
	};

	return (
		<React.Fragment>
			<PageHeader>
				<PageHeader.Title>
					{Language("FLEETS_TITLE")}
					<Helmet title={intl.formatMessage({ id: "FLEETS_TITLE" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{!isReadOnly && (
						<Button onClick={() => navigate("/view/fleets/create")}>
							{Language("ADD_BUTTON")}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			<Card disablePadding>
				<Table
					{...tableProps}
					contentLoading={loading}
					onRowDoubleClick={row => navigate(`/view/fleets/${row.id}/details`)}
					columns={[ {
						id: "description",
						title: intl.formatMessage({ id: "FLEETS_DESCRIPTION" }),
						sortable: true,
						filterComponent: "TEXT",
					}, {
						id: "ships",
						title: intl.formatMessage({ id: "FLEETS_SHIPS" }),
						render: (row: FleetFormQuery) => <FleetShipListPopover ships={row.ships} />,
					} ]}
					rowActions={isReadOnly ? undefined : [ {
						icon: <EditIcon />,
						onClick: (row: FleetFormQuery) => navigate(`/view/fleets/${row.id}/edit`),
					}, {
						icon: <DeleteIcon />,
						hidden: (row) => !!row.externalId,
						onClick: (row: FleetFormQuery) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								deleteFleet(row.id);
							});
						},
					} ]} />
			</Card>
		</React.Fragment>
	);
};

export default Fleets;
